'use client';

import { Fragment, Ref, forwardRef, useState } from 'react';
import NextImage, { ImageProps } from 'next/image';
import cx from 'classnames';

type Brightness = 'high' | 'medium' | 'low';

interface IBrightnessLayer {
    active?: boolean;
    brightness?: Brightness;
}

const BrightnessLayer = ({ active = false, brightness }: IBrightnessLayer) => {
    if (active && brightness) {
        return (
            <div
                className={cx('absolute inset-0 w-full h-full z-20 bg-gradient-to-tr transition-opacity duration-300 pointer-events-none', {
                    'from-black/30 via-black/10 to-black/0': brightness === 'high',
                    'from-black/60 via-black/30 to-black/10': brightness === 'medium',
                    'from-black/90 via-black/50 to-black/20': brightness === 'low'
                })}
            />
        );
    }

    return null;
};

interface IImage extends ImageProps {
    softEntry?: boolean;
    brightness?: Brightness;
}

const Image = forwardRef(({ className = '', softEntry = true, brightness, ...rest }: IImage, ref: Ref<HTMLImageElement|null>) => {
    const [loaded, setLoaded] = useState<boolean>(false);

    return (
        <Fragment>
            <BrightnessLayer
                active={loaded}
                brightness={brightness}
            />
            <NextImage
                ref={ref}
                className={cx({
                    'transition-opacity': softEntry,
                    'opacity-0': softEntry && ! loaded,
                    'opacity-100': softEntry && loaded,
                    'pointer-events-none': ! className.includes('pointer-events-'),
                    [className]: Boolean(className)
                })}
                onLoadingComplete={() => {
                    setLoaded(true);
                }}
                {...rest}
            />
        </Fragment>
    );
});

export { Image };